import React, { useContext } from 'react';
import _ from 'lodash';
import {
  Alert,
  Button as PFButton,
  Tooltip as PFTooltip,
} from '@patternfly/react-core';
import { InfoCircleIcon } from '@patternfly/react-icons';
import { useTranslation } from 'react-i18next';
import { observer, useLocalStore } from 'mobx-react';
import { KalturaWidget, FeatureFlag } from '@ole-ui/ole-ui-components';
import parse from 'html-react-parser';
import { storesContext } from 'stores';
import { KALTURA_CONFIGS } from 'config/constants';
import type UserStore from 'stores/User';
import { LabCardRow } from '../../LabCard';
import LabInstructionsStore from './store';
import type LabEnvironmentStore from '../../store';
import './styles.scss';

type LabInstructionsProps = {
  lab: LabEnvironmentStore;
  user: UserStore;
};

const redirectClassroomWebapp = (url: string) => {
  window.open(url, '_blank');
};

const toRhtlcUrl = (url: string) => url.replace(/^https:/, 'rhtlc:');

const LabInstructions: React.FC<LabInstructionsProps> = ({ lab, user }) => {
  const { t } = useTranslation();
  const store = useLocalStore(() => new LabInstructionsStore());
  const { userStore } = useContext(storesContext);

  const labParamsTooltip = t(
    'Replace the placeholders on the commands above by their respective parameters',
  );

  store.labState = _.get(lab, 'labState');

  const isExpanded = (id: string) => store.expandedPanels?.has(id);
  const {
    labDefinition,
    userCredentialMap,
    publicIp,
    classroomWebapp,
    webApplications,
    hasUserLabParameters,
    usesBastion,
    usesRHTLC,
  } = lab;

  const SSHPortText = hasUserLabParameters ? '<ssh_access_port>' : '53009';

  const isOwnerAccessEnabled = _.has(userCredentialMap, 'owner');

  const classroomWebappUrl = _.get(classroomWebapp, 'url');

  const labOrientationInformation = lab.orientationInformation;
  const labOrientationVideo = lab.orientationVideo;

  const getCredentialsUUID = () => {
    let credentialsUUID = '';

    if (user.isInstructor) {
      credentialsUUID = _.get(userCredentialMap, 'instructor');
    }
    if (!credentialsUUID && user.isAdmin) {
      credentialsUUID =
        _.get(userCredentialMap, 'owner') ||
        _.get(userCredentialMap, 'instructor');
    }

    if (!credentialsUUID) {
      credentialsUUID = _.get(userCredentialMap, 'owner');
    }

    return credentialsUUID;
  };

  const downloadSSHKey = () => {
    const element = document.createElement('a');
    const file = new Blob([store.sshKey], {
      type: 'text/plain',
    });
    element.href = URL.createObjectURL(file);
    element.download = 'rht_classroom.rsa';
    document.body.appendChild(element);
    element.click();
  };

  const labParametersTable = hasUserLabParameters && (
    <>
      <h3>
        {t('Lab parameters')}
        <PFTooltip content={labParamsTooltip}>
          <span className="with-info-icon">
            <InfoCircleIcon />
          </span>
        </PFTooltip>
      </h3>
      <table className="table">
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{t('Description')}</th>
            <th>{t('Value')}</th>
          </tr>
        </thead>

        <tbody>
          {labDefinition.user_lab_parameters.map((paramDefinition) => (
            <tr key={paramDefinition.name}>
              <td>{paramDefinition.name}</td>
              <td>{paramDefinition.title}</td>
              <td>{paramDefinition.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );

  const sshInstructionsBody = (
    <>
      <div className="margin-bottom">
        {t(
          'Do not do this if you have already set a private key up before for a lab.',
        )}
      </div>
      <ol>
        <li>
          <p>
            {t('Click')} <b>{t('CREATE')}</b>{' '}
            {t('to spin up the lab environment')}
          </p>
        </li>
        <li>
          <p>
            {t('Click')} <b>{t('DOWNLOAD SSH KEY')}</b>{' '}
            {t(`button when they're enabled (lab is running)`)}
          </p>
        </li>
        <li>
          <p>{t('Open a terminal in your local machine')}</p>
        </li>
        <li>
          <p>
            {t('On your terminal, go to your ssh folder:')}{' '}
            <code>cd ~/.ssh</code>
          </p>
        </li>
        <li>
          <p>
            {t(
              "If it's not your first time doing this, you can skip this step and go to step 6.",
            )}
            <br />
          </p>
          <ul>
            <li>
              <p>
                {t('Move the downloaded file to')} <code>~/.ssh</code>
                {t(
                  ', either with your files explorer or with the command line:',
                )}
                <br />
                <code>mv ~/Downloads/rht_classroom.rsa ~/.ssh/</code>
                <br />
                <code>chmod 0600 ~/.ssh/rht_classroom.rsa</code>
                <br />
                <code>ssh-add ~/.ssh/rht_classroom.rsa</code>
              </p>
            </li>
          </ul>
        </li>
        <li>
          {usesBastion ? (
            <p>
              {t('ssh into lab:')}{' '}
              <code>
                {t(
                  `ssh -i ~/.ssh/rht_classroom.rsa -J cloud-user@${
                    publicIp || '<public_ip>'
                  }:22022 student@172.25.252.1 -p ${SSHPortText}`,
                )}
              </code>
              {!publicIp && (
                <>
                  <br />
                  {t('Replace')} <code>{t('<public_ip>')}</code>{' '}
                  {t(
                    'on the terminal with the ip that will be presented down below when the labs become active, or wait for said ip to appear automatically in the above command.',
                  )}
                </>
              )}
              <br />
              {t('You will be SSHing into the workstation VM as the')}{' '}
              <em>student</em> {t('user. If you are asked for a password, use')}{' '}
              <code>student</code>
            </p>
          ) : (
            <p>
              {t('Use the key and the')} <em>cloud-user</em>{' '}
              {t('user to jump:')}{' '}
              <code>
                {`ssh -i ~/.ssh/rht_classroom.rsa -J cloud-user@${
                  publicIp || '<public_ip>'
                }:22022 student@workstation`}
              </code>
              {!publicIp && (
                <>
                  <br />
                  {t('Replace')}
                  <code>{' <public_ip> '}</code>
                  {t(
                    'on the terminal with the ip that will be presented down below when the labs become active, or wait for said ip to appear automatically in the above command.',
                  )}
                </>
              )}
            </p>
          )}
        </li>
      </ol>
      <div>{labParametersTable}</div>
    </>
  );

  const labControlsBody = (
    <>
      <p>
        {t('Click')} <b>{t('Create')}</b>{' '}
        {t(
          'to build all of the virtual machines needed for the classroom lab environment. This may take several minutes to complete. Once created the environment can then be stopped and restarted to pause your experience.',
        )}
        <br />
        {t('When a lab is created, click')} <b>{t('Start')}</b>{' '}
        {t('to run all of the virtual machines in the classroom.')}
        <br />
        {t('Click')} <b>{t('Stop')}</b>{' '}
        {t(
          'to stop all the virtual machines from running. This will not delete your lab.',
        )}
        <br />
        {t('If you')} <b>{t('Delete')}</b>{' '}
        {t(
          'your lab, you will remove all of the virtual machines in your classroom and lose all of your progress.',
        )}
        <br />
      </p>

      <p>{t('Virtual machine actions')}</p>
      <p>
        {t('Click')} <b>{t('Start')}</b> {t('to power on the virtual machine.')}
        <br />
        {t('Click')} <b>{t('Shutdown')}</b>{' '}
        {t(
          'to gracefully shut down the virtual machine, preserving disk contents.',
        )}
        <br />
        {t('Click')} <b>{t('Power')}</b>{' '}
        {t(
          'off to forcefully shut down the virtual machine, while still preserving disk contents.',
        )}
        <br />
        {t('Click')} <b>{t('Open console')}</b>{' '}
        {t(
          'to connect to the system console of the virtual machine in a new browser.',
        )}
      </p>

      <p>{t('Auto-stop timer')}</p>
      <p>
        {t(
          'The Red Hat Learning Subscription entitles you to set allotment of lab time.',
        )}
        <br />
        {t(
          'To help conserving your allotted time, the lab environment uses automatic timers to stop or destroy your lab environment when the timer expires.',
        )}
      </p>
      <p>
        {t('Click the')} <b>{t('Auto-stop')}</b>{' '}
        {t(
          'button [+] to extend the time you would like to spend with the labs.',
        )}
        <br />
        {t('Click the')} <b>{t('Auto-destroy')}</b>{' '}
        {t('button [+] to add day(s) to the auto-destroy timer.')}
      </p>

      <p>
        {t(
          'Auto-stop has a maximum of 11 hours, and auto-destroy has a maximum of {{autodestroyMaxDays}} days.',
          {
            autodestroyMaxDays: lab.labDeleteThreshold,
          },
        )}
        <br />
        {t(
          "Be careful to keep the timers set while you are working, so that your environment doesn't shut down unexpectedly.",
        )}
        <br />
        {t(
          'We also suggest not to set the auto-timers unnecessarily high, which could waste your lab time allotment',
        )}
      </p>
    </>
  );

  const canDownloadSshKey = Boolean(
    lab.isUserLabReady && userCredentialMap && getCredentialsUUID(),
  );

  const handleDownloadSshKey = () => {
    const creds = getCredentialsUUID();
    if (!store.sshKey) {
      store.getSSHkey(creds, 'downloaded').then(downloadSSHKey);
    } else {
      downloadSSHKey();
    }
  };

  return (
    <>
      {store.isTourDisplayed && (
        <div className="tour-wrapper">
          <div className="overlay">
            <div className="overlay-content">
              <KalturaWidget
                className="expert-extras-player"
                partnerId={KALTURA_CONFIGS.partnerId}
                playerId={KALTURA_CONFIGS.labsTutorial.uiConfId}
                entryId={KALTURA_CONFIGS.labsTutorial.entryId}
                isPlaylist
              />
              <div className="text-center">
                <PFButton variant="danger" onClick={store.toggleTour}>
                  {t('Close video')}
                </PFButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {store.isOrientationVideoDisplayed && labOrientationVideo && (
        <div className="tour-wrapper">
          <div className="overlay">
            <div className="overlay-content">
              <KalturaWidget
                className="expert-extras-player"
                partnerId={KALTURA_CONFIGS.partnerId}
                playerId={lab.orientationVideoPlayerId}
                entryId={labOrientationVideo.entry_id}
                isPlaylist={false}
              />
              <div className="text-center">
                <PFButton
                  variant="danger"
                  onClick={store.toggleOrientationVideoDisplayed}
                >
                  {t('Close video')}
                </PFButton>
              </div>
            </div>
          </div>
        </div>
      )}

      <FeatureFlag
        section="lab"
        flagName="lab_control_instructions"
        defaultFlagValue
      >
        <LabCardRow
          label={t('Lab controls')}
          actionLabel={t('Watch tutorial')}
          onActionClick={store.toggleTour}
          isExpanded={isExpanded('lab-info')}
          onToggle={() =>
            store.togglePanels('lab-info', !isExpanded('lab-info'))
          }
          analyticsId="watch-tutorial-vt-dash-lp"
        >
          {labControlsBody}
        </LabCardRow>
      </FeatureFlag>

      {labOrientationInformation && (
        <FeatureFlag
          section="lab"
          flagName="lab_orientation_instructions"
          defaultFlagValue
        >
          <LabCardRow
            label={t('Orientation to the classroom environment')}
            actionLabel={
              labOrientationVideo ? t('Watch orientation') : undefined
            }
            onActionClick={
              labOrientationVideo
                ? () => {
                    store.toggleOrientationVideoDisplayed();
                  }
                : undefined
            }
            isExpanded={isExpanded('lab-ori-info')}
            onToggle={() =>
              store.togglePanels('lab-ori-info', !isExpanded('lab-ori-info'))
            }
            analyticsId="watch-tutorial-orientation-dash-lp"
          >
            <p>{parse(labOrientationInformation)}</p>
          </LabCardRow>
        </FeatureFlag>
      )}

      {(userStore.isInstructorOrAdmin || isOwnerAccessEnabled) && (
        <LabCardRow
          label={t('SSH private key & instructions')}
          actionLabel={t('Download SSH key')}
          onActionClick={handleDownloadSshKey}
          actionDisabled={!canDownloadSshKey}
          isExpanded={isExpanded('ssh-key-info')}
          onToggle={() =>
            store.togglePanels('ssh-key-info', !isExpanded('ssh-key-info'))
          }
          analyticsId="ssh-acc-vt-dash-lp"
        >
          {sshInstructionsBody}
        </LabCardRow>
      )}

      {webApplications.length === 0 && classroomWebappUrl && (
        <LabCardRow
          label={t('Classroom webapp')}
          actionLabel={t('Access classroom webapp')}
          onActionClick={() => redirectClassroomWebapp(classroomWebappUrl)}
          isExpanded={isExpanded('classroom-webapp-info')}
          onToggle={() =>
            store.togglePanels(
              'classroom-webapp-info',
              !isExpanded('classroom-webapp-info'),
            )
          }
          analyticsId="lab-ctrl-acc-vt-dash-lp"
        >
          <p>
            {t(
              'Classroom web application consoles are more appropriate for a command line experience, including cutting and pasting command text, uploading files into the environments, and related activities.',
            )}
          </p>
          <p>
            {t(
              'The standard VNC console approach is more appropriate for graphical applications and remote desktop environments.',
            )}
          </p>
        </LabCardRow>
      )}

      {usesRHTLC && classroomWebappUrl && (
        <LabCardRow
          label={t('Red Hat Training Lab Connector (RHTLC)')}
          isExpanded={isExpanded('rhtlc-info')}
          onToggle={() =>
            store.togglePanels('rhtlc-info', !isExpanded('rhtlc-info'))
          }
          analyticsId="rhtlc-acc-vt-dash-lp"
        >
          <p>
            {t(
              'The RHTLC client provides GUI and CLI interfaces that enable students and instructors to connect to the online lab environments. The connection is established through a WebSockets tunnel and enables SSH, SFTP, SOCKS5 proxy, RDP (in some instances), and VPN connectivity for users to the lab environments. This method enhances the student experience and assists with learning. After installation, students can click the "Launch RHTLC" button to open the RHTLC application and establish a connection to the lab environment.',
            )}
          </p>
          <p>
            {t(
              'After establishing a connection, students can use the GUI to create an SSH connection directly to the',
            )}{' '}
            <b>{t('workstation')}</b>{' '}
            {t(
              'VM or create a VPN connection so that their system is on the same network as the lab environment. In this way, web browsers and other tools can be used directly, which eliminates the need to access the environment through the VNC console.',
            )}
          </p>
          <p>
            {t('For more information and installation instructions, refer to')}{' '}
            <a
              href="https://redhattraining.github.io/RHTLC_Docs/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('RHTLC Installation Guide')}
            </a>
            .
          </p>
          <div className="rhtlc-actions">
            <PFButton
              variant="danger"
              onClick={() => {
                navigator.clipboard.writeText(toRhtlcUrl(classroomWebappUrl));
              }}
            >
              {t('Copy RHTLC URL')}
            </PFButton>
            <PFButton
              variant="danger"
              onClick={() => {
                window.open(toRhtlcUrl(classroomWebappUrl), '_blank');
              }}
            >
              {t('Launch RHTLC')}
            </PFButton>
          </div>
        </LabCardRow>
      )}

      {userStore.isInstructorOrAdmin &&
        store.labState === 'running' &&
        !userCredentialMap && (
          <Alert
            isInline
            variant="danger"
            className="no-ssh-banner"
            title={t('SSH is not enabled for this lab')}
          />
        )}
    </>
  );
};

export default observer(LabInstructions);
